import {
  Body,
  Button,
  CheckButton,
  CheckButtonGroup,
  Flex,
  Grid,
  Heading,
  SimpleGrid,
  Stack,
  TextInput,
} from '@hover/blueprint';
import React, { useContext, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useHistory } from 'react-router';
import { type InferType } from 'yup';

import { OnboardingFormFooter } from 'src/modules/onboarding/components/OnboardingFormFooter';
import { makeOrgDescriptionValidationSchema } from 'src/modules/onboarding/validations/makeValidationSchemas';
import { useIsMobileSize } from 'src/modules/renderings/hooks';
import { Form as SharedForm } from 'src/shared/components/forms';
import { OnboardingSignupTypes } from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useEffectOnMount,
  useTranslate,
} from 'src/shared/hooks';
import { Route as Routes } from 'src/shared/route';
import { buttonPressed, pageViewed } from 'src/shared/telemetry';

import {
  OrgConstructionDescriptions,
  OrgInsuranceDescriptions,
} from '../validations/validations';

import { OnboardingContext } from './OnboardingContext';

export type UserOrgFormData = InferType<
  ReturnType<typeof makeOrgDescriptionValidationSchema>
>;

interface OrgFormData {
  orgDescriptions: (OrgConstructionDescriptions | OrgInsuranceDescriptions)[];
  customDescription?: string;
}

interface UserOrgDescriptionFormFieldsProps {
  skip: () => void;
}

const UserOrgDescriptionFormFields = ({
  skip,
}: UserOrgDescriptionFormFieldsProps) => {
  const t = useTranslate(TranslationNamespace.SHARED);

  const isMobile = useIsMobileSize();

  const [contextState] = useContext(OnboardingContext);

  const orgConstructionDescriptionOptions = [
    {
      value: OrgConstructionDescriptions.NEW_CONSTRUCTION,
      text: t('onboarding.newConstruction'),
    },
    {
      value: OrgConstructionDescriptions.ROOFING,
      text: t('onboarding.roofing'),
    },
    {
      value: OrgConstructionDescriptions.ARCHITECT_DESIGNER,
      text: t('onboarding.architectDesigner'),
    },
    {
      value: OrgConstructionDescriptions.WINDOWS_AND_DOORS,
      text: t('onboarding.windowsAndDoors'),
    },
    {
      value: OrgConstructionDescriptions.INTERIOR_TRADES,
      text: t('onboarding.interiorTrades'),
    },
    {
      value: OrgConstructionDescriptions.SIDING,
      text: t('onboarding.siding'),
    },
    {
      value: OrgConstructionDescriptions.DISTRIBUTOR_MANUFACTURER,
      text: t('onboarding.distributorManufacturer'),
    },
  ];

  const orgInsuranceDescriptionOptions = [
    {
      value: OrgInsuranceDescriptions.CARRIER,
      text: t('onboarding.carrier'),
    },
    {
      value: OrgInsuranceDescriptions.CLAIMS_ADJUSTING_FIRM,
      text: t('onboarding.claimsAdjustingFirm'),
    },
    {
      value: OrgInsuranceDescriptions.INDEPENDENT_ADJUSTER,
      text: t('onboarding.independentAdjuster'),
    },
    {
      value: OrgInsuranceDescriptions.INSPECTION_FIRM,
      text: t('onboarding.inspectionFirm'),
    },
    {
      value: OrgInsuranceDescriptions.INDEPENDENT_INSPECTOR,
      text: t('onboarding.independentInspector'),
    },
  ];

  const orgDescriptionOptions =
    contextState.orgType === OnboardingSignupTypes.Insurance
      ? orgInsuranceDescriptionOptions
      : orgConstructionDescriptionOptions;

  const [otherOpen, setOtherOpen] = useState(false);

  return (
    <Stack
      direction={{ base: 'column', tablet: 'row' }}
      flex={1}
      flexWrap="nowrap"
      justifyContent={{ base: 'start', tablet: 'space-around' }}
      marginTop={300}
      paddingX={{ base: 0, tablet: '10%' }}
      spacing={0}
    >
      <Flex
        flexBasis={{ base: 'auto', tablet: 750 }}
        flexGrow="1"
        flexShrink="1"
        mb={{ base: 400, tablet: 0 }}
      >
        <Flex flexDir="column" maxW={750}>
          <Heading
            mb={{ base: 400, tablet: 200 }}
            size={{ base: 700, tablet: 800 }}
          >
            {t('onboarding.howDoYouDescribe')}
          </Heading>
          <Body color="neutral.600" mt={0}>
            {t('onboarding.selectAllTheOptions')}
          </Body>
        </Flex>
      </Flex>
      <Flex
        alignItems="center"
        flexBasis={750}
        flexDir="column"
        flexGrow="2"
        flexShrink="0"
      >
        <Controller
          name="orgDescriptions"
          render={props => (
            <SimpleGrid
              columns={{ base: 1, tablet: 2 }}
              height="fit-content"
              spacingX={500}
              spacingY={300}
              width={1}
            >
              <CheckButtonGroup
                onChange={val => {
                  props.onChange(val);
                }}
              >
                {orgDescriptionOptions.map(option => (
                  <CheckButton
                    borderRadius="20px"
                    key={option.value}
                    overflowWrap="break-word"
                    size="large"
                    textAlign="center"
                    value={option.value}
                    whiteSpace="break-spaces"
                  >
                    {option.text}
                  </CheckButton>
                ))}
              </CheckButtonGroup>
              <CheckButton
                borderRadius="20px"
                onChange={() => {
                  setOtherOpen(!otherOpen);
                }}
                size="large"
              >
                {t('onboarding.other')}
              </CheckButton>
            </SimpleGrid>
          )}
        />
        {otherOpen ? (
          <Controller
            name="customDescription"
            render={props => (
              <TextInput
                bgColor="neutral.100"
                borderColor="neutral.100"
                borderRadius="15px"
                mt={500}
                onChange={val => props.onChange(val)}
                placeholder={t('onboarding.describeYourBusinessHere')}
              />
            )}
          />
        ) : (
          !isMobile && (
            <Button mt={600} onClick={skip} variant="ghost" width="fit-content">
              {t('onboarding.skip')}
            </Button>
          )
        )}
      </Flex>
    </Stack>
  );
};

export const UserOrgDescription = () => {
  const [, contextDispatch] = useContext(OnboardingContext);
  const validationSchema = makeOrgDescriptionValidationSchema();
  const initialValues = { orgDescriptions: [] };

  const history = useHistory();

  const navigateToUsage = () =>
    history.push(`${Routes.SIGN_UP_ONBOARDING.path}/usage`);

  const skip = () => {
    buttonPressed({
      feature: 'onboarding',
      page_or_screen_name: 'Onboarding-persona_input',
      button_text: 'Skip',
    });
    navigateToUsage();
  };

  useEffectOnMount(() => {
    pageViewed({
      page_or_screen_name: 'Onboarding-persona',
      feature: 'onboarding',
    });
  });

  const onSubmit = (formData: OrgFormData) => {
    const { orgDescriptions } = formData;
    buttonPressed({
      feature: 'onboarding',
      page_or_screen_name: 'Onboarding-persona_input',
      button_text: 'Next',
      // @ts-ignore
      selection: orgDescriptions,
    });
    contextDispatch(formData);
    navigateToUsage();
  };

  return (
    <Grid alignItems="stretch" flex={1} width={1}>
      <SharedForm<UserOrgFormData>
        display="flex"
        height="100%"
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        <UserOrgDescriptionFormFields skip={skip} />
        <OnboardingFormFooter hideForMobile={false} />
      </SharedForm>
    </Grid>
  );
};
